import type { FC } from "react"
import { useCallback, useContext, useEffect } from "react"

import { nanoid } from "nanoid"
import {
  atom,
  useRecoilState,
  useRecoilCallback,
  useSetRecoilState,
} from "recoil"
import styled from "styled-components"

import { Modal, usePortal } from "~/components/ui"
import { AirbrakeContext } from "~/contexts"
import EnvVars from "~/env-vars"
import type { ReturnedGraphQLError } from "~/utils/graphql"
import {
  isGraphQLResponseError,
  isUnauthenticatedError,
} from "~/utils/handleGraphQLError"

import {
  QUERY_ERROR_CODES,
  queryErrorNotificationState,
} from "./queryErrorNotifications"
import Button from "../../../../components/Button"

export const queryErrors = atom<
  { error: OnErrorReturnedError | unknown; referenceId: string }[]
>({
  key: `queryErrors`,
  default: [],
})

export type OnErrorReturnedError = {
  request: { query: string; variables: Record<string, unknown> }
  response: {
    data: null
    errors: ReturnedGraphQLError[]
    headers: Headers
    status: number
  }
}

function isReturnedQueryError(error: unknown): error is OnErrorReturnedError {
  return (
    typeof error === `object` &&
    error !== null &&
    `request` in error &&
    `response` in error &&
    typeof error.request === `object` &&
    error.request !== null &&
    `query` in error.request &&
    `variables` in error.request &&
    typeof error.response === `object` &&
    error.response !== null &&
    `data` in error.response &&
    `errors` in error.response &&
    Array.isArray(error.response.errors) &&
    `headers` in error.response &&
    typeof error.response.headers === `object` &&
    error.response.headers !== null &&
    `status` in error.response &&
    typeof error.response.status === `number`
  )
}

export const notificationBarIsOpenState = atom<boolean>({
  key: `notificationBarIsOpen`,
  default: true,
})

export const useDispatchError = (): ((error: unknown) => void) => {
  const { airbrake } = useContext(AirbrakeContext)
  const setQueryErrorNotifications = useSetRecoilState(
    queryErrorNotificationState,
  )
  const setIsNotificationBarOpen = useSetRecoilState(notificationBarIsOpenState)
  const setModalErrors = useRecoilCallback(
    ({ set }) =>
      async (error: unknown) => {
        set(queryErrors, (cur) => [
          ...cur,
          {
            error,
            referenceId:
              isReturnedQueryError(error) &&
              error.response.errors[0].extensions?.errorId
                ? error.response.errors[0].extensions?.errorId
                : nanoid(),
          },
        ])
      },
  )

  const updateQueryErrorNotifications = useCallback(
    async (error: unknown) => {
      const allErrors =
        // @ts-expect-error - won't let me access fields on the error
        error.response?.errors.map((e: any) => e?.extensions ?? {}) ?? []

      const everyErrorIsNotQueryError = allErrors.every(
        (e: any) => !QUERY_ERROR_CODES.includes(e.error),
      )
      if (everyErrorIsNotQueryError) {
        await setModalErrors(error)

        return
      }
      console.log(`all query errors: `, allErrors)
      setQueryErrorNotifications((prev) => {
        const filteredErrors = allErrors
          .filter(
            (error: any) =>
              !prev.some(
                (prevError) =>
                  prevError.code === error.code &&
                  prevError.message === error.message,
              ),
          )
          .map((e: any) => ({
            code: e.error,
            message: e.message,
          }))

        return [...prev, ...filteredErrors]
      })
      setIsNotificationBarOpen(true)
    },
    [setModalErrors, setQueryErrorNotifications, setIsNotificationBarOpen],
  )

  const callback = useCallback(
    async (error: unknown) => {
      if (isGraphQLResponseError(error)) {
        if (isUnauthenticatedError(error)) {
          console.warn(`Unauthenticated error, redirecting to login`)
          localStorage.setItem(`jwt`, ``)
          window.location.replace(`${EnvVars.API_URL}/auth/sso/login`)

          return
        }
      }

      void updateQueryErrorNotifications(error)
      if (airbrake) {
        await airbrake.notify(
          isReturnedQueryError(error)
            ? error.response?.errors.map((e) => ({
                error: e.message,
                params: {
                  query: error.request.query,
                  variables: error.request.variables,
                },
              }))
            : error,
        )
      }
    },
    [airbrake, updateQueryErrorNotifications],
  )

  return callback
}

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 60vh;
  padding: 20px;
  gap: 20px;
  header {
    color: var(--blue-dark-font);
    h2 {
      font-weight: 500;
    }
  }
  section {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-left: 20px;
    p {
      font-size: 16px;
      font-weight: 500;
    }
    a {
      font-size: 14px;
    }
    button {
      width: 130px;
      align-self: flex-end;
    }
  }
`
export const QueryErrorModal: FC = () => {
  const { Portal, show, hide } = usePortal({ elementId: `error-modal` })
  const [errors, setErrors] = useRecoilState(queryErrors)

  const currentError = errors[0]

  const resolveError = () => {
    setErrors((errors) => errors.slice(1))
  }

  useEffect(() => {
    if (currentError) {
      show()
    } else {
      hide()
    }
  }, [currentError, show, hide])

  return (
    <Portal>
      <Modal onClose={() => resolveError()}>
        <Wrapper>
          <header>
            <h1>Something went wrong, sorry for the inconvenience</h1>
            <h2>Please reload the page to try again</h2>
          </header>
          <section>
            <p>
              Reference #:{` `}
              {(currentError as { referenceId?: string; error?: unknown })
                ?.referenceId ?? ``}
            </p>
            <a
              href={`mailto:support@powur.com?subject=PowurVisionError:${
                (
                  currentError as {
                    referenceId?: string
                    error?: unknown
                  }
                )?.referenceId ?? ``
              }`}
            >
              Report this issue to the Vision support team
            </a>
            <Button variant="solid" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          </section>
        </Wrapper>
      </Modal>
    </Portal>
  )
}
